import React from "react";
import { Box, Stack } from "@mui/material";
import CompactDateFilter from "../DateFilter";
import { boxShadows, transparentColors } from "../../utils/theme";

export interface PageToolbarProps {
  /** Show date filter */
  showDateFilter?: boolean;
  /** Additional custom actions */
  children?: React.ReactNode;
  /** View context for contextual date display (daily/monthly/yearly) */
  viewContext?: "daily" | "monthly" | "yearly";
  /** Callback to change view when preset is selected */
  onViewChange?: (view: "daily" | "monthly" | "yearly") => void;
}

/**
 * PageToolbar - Sticky toolbar for page-level actions
 *
 * Contains date filter and custom actions
 * Stays visible when scrolling for easy access to filters
 */
const PageToolbar: React.FC<PageToolbarProps> = ({
  showDateFilter = true,
  children,
  viewContext,
  onViewChange,
}) => {
  return (
    <Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 100,
        background:
          "linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)",
        color: "white",
        py: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 3 },
        boxShadow: boxShadows.toolbar,
        backdropFilter: "blur(10px)",
        borderBottom: `1px solid ${transparentColors.white10}`,
        transition: "top 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <Stack
        direction="row"
        spacing={{ xs: 1.5, sm: 2 }}
        alignItems="center"
        sx={{
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: { xs: 1, sm: 0 },
        }}
      >
        {/* Date Filter */}
        {showDateFilter && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              order: { xs: 1, sm: 1 },
            }}
          >
            <CompactDateFilter
              viewContext={viewContext}
              onViewChange={onViewChange}
            />
          </Box>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1, order: { xs: 3, sm: 2 } }} />

        {/* Custom Actions */}
        {children && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              order: { xs: 2, sm: 3 },
            }}
          >
            {children}
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default PageToolbar;
